// Can this order be created yet?
//
// Every product has to be planned to 100% before an order is written: the
// delivery plan is what the floor launches against, and a line with no plan is
// a promise nobody can act on.
//
// The rule was already enforced on submit. What was missing is that it was
// only ever SAID on submit — the error renders near the top of the form and
// the Create Order button is at the bottom, a long form away. Click it with an
// unplanned product and the message appears off-screen: the button looks
// broken rather than refused.
//
// So the same predicate now drives the button as well, and a product that is
// not ready is named before anyone reaches for it.

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const productLabel = p =>
  `${p?.cableName ?? '?'} ${p?.numberOfConductors ?? '?'}x${p?.section ?? '?'}`;

/**
 * Products that still need a delivery plan.
 *
 * "From stock" is exempt by design: it is fulfilled from the warehouse, with
 * whatever is short staying to-produce, so it never carries a plan.
 *
 * @returns [{ product, label, ordered, planned, missing }]
 */
export function unplannedProducts(products = []) {
  const list = Array.isArray(products) ? products : [];
  return list
    .filter(p => p && !p.fromStock)
    .map(p => {
      const ordered = num(p.totalOrderedLength);
      const planned = num(p?.deliveryPlan?.plannedTotal);
      return { product: p, label: productLabel(p), ordered, planned, missing: ordered - planned };
    })
    // A product with no ordered length is not planned either — `total > 0` was
    // part of the original rule and dropping it would let an empty line through.
    .filter(x => !(x.ordered > 0 && x.planned >= x.ordered));
}

/**
 * Why the order cannot be created, or null when it can.
 *
 * One sentence, because it is shown next to the button and read in passing.
 */
export function orderBlockedReason(products = []) {
  // Falsy entries dropped BEFORE the emptiness check: a list of two nulls has
  // length 2 and would otherwise sail through as "two planned products".
  const list = (Array.isArray(products) ? products : []).filter(Boolean);
  if (!list.length) return 'Add at least one product before creating the order.';

  const notPlanned = unplannedProducts(list);
  if (!notPlanned.length) return null;

  return (
    `${notPlanned.length} product(s) are not fully planned: ` +
    notPlanned
      .map(x =>
        x.ordered > 0
          ? `${x.label} (${Math.round(x.planned).toLocaleString()} of ${Math.round(x.ordered).toLocaleString()} m)`
          : `${x.label} (no length)`
      )
      .join(', ') +
    '. Add a complete delivery plan for each, or mark it "From stock".'
  );
}

export const canCreateOrder = products => orderBlockedReason(products) === null;
